import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Brand from "../components/Brand";
import {
  ArrowLeft, Loader2, Printer, Download, Search, Heart, Sparkles,
} from "lucide-react";

/* /coloring-pages — 52 free Bible-story coloring pages for kids.
   Each card has its own thumbnail + Print This One + Download buttons,
   so visitors can pick exactly the one they want (no obligation to take
   all 52). Each PNG already has a Faitharama.com footer burned on top
   of the original Gospel Light copyright credit (which gives us
   "permission to photocopy" rights). */

const G = "#2E9F3A";
const O = "#F39A1F";
const WINE = "#0F172A";

const KIDS_HERO = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/zu1m7jpm_CHILDREN%20COLORING.png";

function resolveImage(u) {
  if (!u) return "";
  if (/^(https?:|data:)/i.test(u)) return u;
  if (u.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export default function ColoringPagesPage({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null); // currently-opened page in the modal
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.get("/coloring-pages")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const goBack = () => {
    if (window.history.length > 1 && loc.key !== "default") nav(-1);
    else nav("/");
  };

  const filtered = query.trim()
    ? items.filter((p) =>
        (p.title || "").toLowerCase().includes(query.toLowerCase()) ||
        (p.scripture_ref || "").toLowerCase().includes(query.toLowerCase())
      )
    : items;

  /* PRINT — uses a hidden iframe rather than window.open so modern
     browsers' popup blockers DON'T silently kill the action (the previous
     window.open approach was being killed by Chrome/Safari). */
  const printOne = (page) => {
    const img = resolveImage(page.image_url);
    if (!img) return;
    // Reuse the same hidden iframe each time
    let iframe = document.getElementById("coloring-print-iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "coloring-print-iframe";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.visibility = "hidden";
      document.body.appendChild(iframe);
    }
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><title>${page.title} — Faitharama.com</title>
      <style>
        @page { size: letter portrait; margin: 0.4in; }
        html,body { margin:0; padding:0; background:#fff; }
        .wrap { display:flex; align-items:flex-start; justify-content:center; padding:0; }
        img { max-width: 100%; height: auto; display:block; }
      </style></head>
      <body><div class="wrap"><img id="p" src="${img}" alt="${page.title}"/></div>
      <script>
        var im=document.getElementById('p');
        function go(){ try{ window.focus(); window.print(); }catch(e){} }
        if(im.complete){ go(); } else { im.onload=go; im.onerror=go; }
      </script>
      </body></html>`);
    doc.close();
  };

  const printAll = () => {
    if (!items.length) return;
    let iframe = document.getElementById("coloring-print-iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "coloring-print-iframe";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.visibility = "hidden";
      document.body.appendChild(iframe);
    }
    const imgs = items.map((p) =>
      `<img src="${resolveImage(p.image_url)}" alt="${p.title}" style="width:100%;height:auto;page-break-after:always;display:block;margin-bottom:14px"/>`
    ).join("");
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><title>All 52 Coloring Pages — Faitharama.com</title>
      <style>
        @page { size: letter portrait; margin: 0.4in; }
        html,body { margin:0; padding:0; background:#fff; }
        .wrap { padding:0; }
        img { width:100%; height:auto; page-break-after: always; display:block; }
      </style></head>
      <body><div class="wrap">${imgs}</div>
      <script>window.addEventListener('load', function(){ setTimeout(function(){ window.focus(); window.print(); }, 800); });</script>
      </body></html>`);
    doc.close();
  };

  /* SAVE — uses fetch→Blob→object-URL so cross-origin downloads actually
     trigger a "save as" dialog instead of opening the PNG inline (the old
     <a download> attribute is ignored across origins). */
  const saveOne = async (page) => {
    const url = resolveImage(page.image_url);
    if (!url) return;
    try {
      const r = await fetch(url, { credentials: "omit" });
      const blob = await r.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj;
      a.download = `${page.slug}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free the blob URL after a short delay so the download has a chance to start
      setTimeout(() => URL.revokeObjectURL(obj), 4000);
    } catch (err) {
      // Fall back to opening the image in a new tab so visitor can right-click → save
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }} data-testid="coloring-page">
      {!embedded && <SiteNav />}

      {/* Floating back — bright, NOT dark */}
      {!embedded && (<button onClick={goBack} data-testid="coloring-back"
              className="fixed top-20 left-3 md:left-6 z-30 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs uppercase tracking-[0.22em] font-bold shadow-md hover:scale-[1.04] transition"
              style={{ background: "#FFFFFF", color: WINE, border: `2px solid ${O}` }}>
        <ArrowLeft className="w-4 h-4" /> Back
      </button>)}

      {/* ═══ TIGHT HERO — photo + slogan, NO instructions, NO bloat ═══ */}
      <section className="relative overflow-hidden"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #D9EEFB 50%, #EAF6FC 100%)" }}>
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-10">
          <div className="grid md:grid-cols-[1fr_360px] gap-6 md:gap-8 items-center">
            {/* Left — copy (short) */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.28em] uppercase font-bold mb-3 text-white"
                   style={{ background: G }} data-testid="coloring-hero-eyebrow">
                <Sparkles className="w-3.5 h-3.5" /> 52 FREE BIBLE COLORING PAGES
              </div>
              <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.04] uppercase"
                  style={{ color: WINE, textWrap: "balance" }}>
                Free Coloring Pages<br/>For The Grandkids.
              </h1>
              <p className="text-base md:text-lg mt-4 max-w-xl mx-auto md:mx-0"
                 style={{ color: "#0F172A" }}>
                Tap any picture below to see it big. Then print it.
              </p>
            </div>

            {/* Right — kids photo, smaller so it doesn't eat the whole screen */}
            <div className="mx-auto md:mx-0" data-testid="coloring-kids-hero">
              <div className="rounded-3xl overflow-hidden shadow-xl"
                   style={{ border: `4px solid ${O}` }}>
                <img
                  src={KIDS_HERO}
                  alt="Two children colouring together"
                  className="block w-full h-auto"
                  style={{ maxWidth: 360 }}
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEARCH BAR */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-4 relative z-20" data-testid="coloring-search-section">
        <div className="bg-white rounded-xl shadow-md border-2 p-3 md:p-4 flex items-center gap-3"
             style={{ borderColor: `${G}55` }}>
          <Search className="w-5 h-5 shrink-0 ml-1" style={{ color: G }} />
          <input
            data-testid="coloring-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by story or verse — e.g. 'Noah', 'David', 'Genesis'"
            className="flex-1 outline-none text-base md:text-lg bg-transparent placeholder:text-foreground/40"
          />
          <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-foreground/60 shrink-0 hidden sm:block">
            {filtered.length} / {items.length}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section id="grid" className="max-w-6xl mx-auto px-5 md:px-8 py-6 md:py-8" data-testid="coloring-grid">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: O }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-foreground/60">
            <p className="text-lg italic">No matching stories. Try a different word.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Card key={p.slug} page={p}
                    onOpen={() => setPreview(p)}
                    onPrint={() => printOne(p)}
                    onSave={() => saveOne(p)} />
            ))}
          </div>
        )}
      </section>

      {/* FOOTER CTA — kept tiny: just one quiet "share this link" line, no buttons */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pb-10 text-center" data-testid="coloring-share-line">
        <p className="text-sm italic text-foreground/60">
          ★ Free to pass on — send <strong>faitharama.com/coloring-pages</strong> to any family who needs it.
        </p>
      </section>

      {/* ═══ PREVIEW MODAL — shows the picked page big, with working buttons ═══ */}
      {preview && (
        <PreviewModal
          page={preview}
          onClose={() => setPreview(null)}
          onPrint={() => printOne(preview)}
          onSave={() => saveOne(preview)}
        />
      )}

      {!embedded && <SiteFooter />}
    </div>
  );
}

/* ─── Preview modal — the BIG image so visitors see what they're picking ─── */
function PreviewModal({ page, onClose, onPrint, onSave }) {
  const img = resolveImage(page.image_url);

  useEffect(() => {
    // Lock body scroll while the modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="coloring-preview-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      style={{ background: "rgba(31,26,23,0.78)" }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto"
        style={{ border: `4px solid ${O}` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 p-4 md:p-5 border-b-2"
             style={{ background: "#FFFFFF", borderColor: `${O}33` }}>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: O }}>
              ★ Story #{page.order}
            </div>
            <h2 className="font-serif-display text-xl md:text-2xl uppercase leading-tight truncate"
                style={{ color: WINE }}>{page.title}</h2>
            {page.scripture_ref && (
              <div className="text-xs md:text-sm italic mt-0.5" style={{ color: WINE }}>{page.scripture_ref}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="coloring-preview-close"
            aria-label="Close preview"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-lg hover:scale-110 transition"
            style={{ background: WINE, color: "#FFFFFF" }}
          >
            ×
          </button>
        </div>

        {/* Big image */}
        <div className="p-4 md:p-6 flex items-center justify-center" style={{ background: "#FFFFFF" }}>
          <img
            src={img}
            alt={page.title}
            className="block max-w-full h-auto rounded-md shadow-md"
            style={{ border: `2px solid ${O}55` }}
            data-testid="coloring-preview-image"
          />
        </div>

        {/* Action buttons */}
        <div className="sticky bottom-0 p-4 md:p-5 border-t-2 grid grid-cols-2 gap-3"
             style={{ background: "#FFFFFF", borderColor: `${O}33` }}>
          <button onClick={onPrint} data-testid="coloring-preview-print"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm md:text-base font-bold uppercase tracking-[0.14em] text-white hover:scale-[1.03] transition shadow"
                  style={{ background: G }}>
            <Printer className="w-4 h-4" /> Print This Page
          </button>
          <button onClick={onSave} data-testid="coloring-preview-save"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm md:text-base font-bold uppercase tracking-[0.14em] text-white hover:scale-[1.03] transition shadow"
                  style={{ background: O }}>
            <Download className="w-4 h-4" /> Save To Computer
          </button>
        </div>
      </div>
    </div>
  );
}

function Card({ page, onOpen, onPrint, onSave }) {
  const img = resolveImage(page.image_url);
  return (
    <article
      data-testid={`coloring-card-${page.slug}`}
      className="bg-white rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl transition-all hover:-translate-y-1"
      style={{ borderColor: `${O}40` }}
    >
      {/* Whole thumbnail is clickable — opens the BIG preview */}
      <button type="button" onClick={onOpen}
              data-testid={`coloring-open-${page.slug}`}
              aria-label={`Open preview for ${page.title}`}
              className="block w-full aspect-[3/4] overflow-hidden bg-[#FFFFFF] cursor-zoom-in relative group">
        <img
          src={img}
          alt={page.title}
          loading="lazy"
          className="block w-full h-full object-contain transition-transform duration-200 group-hover:scale-[1.04]"
        />
        {/* Hover hint */}
        <div className="absolute inset-x-0 bottom-0 px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-bold text-center opacity-0 group-hover:opacity-100 transition"
             style={{ background: `${WINE}DD`, color: "#FFFFFF" }}>
          ★ Tap To Preview Big
        </div>
      </button>

      <div className="p-4 md:p-5">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: O }}>
          ★ Story #{page.order}
        </div>
        <h3 className="font-serif-display text-xl md:text-2xl leading-tight mt-1.5 uppercase"
            style={{ color: WINE, textWrap: "balance" }}>
          {page.title}
        </h3>
        {page.scripture_ref && (
          <div className="text-xs md:text-sm italic text-foreground/70 mt-1.5">
            {page.scripture_ref}
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button type="button" onClick={onPrint}
                  data-testid={`coloring-print-${page.slug}`}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-md text-sm font-bold uppercase tracking-[0.14em] text-white hover:scale-[1.03] transition shadow"
                  style={{ background: G }}>
            <Printer className="w-4 h-4" /> Print
          </button>
          <button type="button" onClick={onSave}
                  data-testid={`coloring-download-${page.slug}`}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-md text-sm font-bold uppercase tracking-[0.14em] text-white hover:scale-[1.03] transition shadow"
                  style={{ background: O }}>
            <Download className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </article>
  );
}
